import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { Drawer, Button } from "@mui/material";
import { Tooltip } from "react-tooltip";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import AvatarImg from "../../../img/app/avatar_default_th.jpg";
import {
  ApprovedIcon,
  PendingIcon,
  RejectedIcon,
  CircleOffIcon,
  CircleArrowUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  InfoTooltipIcon,
} from "../../../components/commonUI/Icons/SvgIcon";
import { serverUrl, userImagesFolder, thumbSuffix, imageFormat, defaultAvatarImage } from "../../../config/env";
import PropTypes from "prop-types";

const DriverPayEligibilityTab = ({
  issueResolutions,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onReset,
  filters,
  sort,
  onSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  inputValue,
  setInputValue,
  state,
  toggleDrawer,
  DriverPayEligibilityProps,
}) => {
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "sorting-icon sorting-icon-up";
    } else if (sort === `${key}=-1`) {
      return "sorting-icon sorting-icon-down";
    }
    return "";
  };

  const getJobTypeDisplay = (jobType) => {
    const jobTypeMap = {
      getitems: "Get items",
      housemove: "House move",
      senditems: "Send items",
      wasteDisposal: "Waste Disposal",
    };
    return jobTypeMap[jobType] || jobType;
  };

  const getDriverAvatarSrc = useCallback((issueResolution) => {
    if (
      issueResolution?.driverDetails?.avatar &&
      issueResolution?.driverDetails.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${issueResolution?.driverDetails?.shortId}/${issueResolution?.driverDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const renderEligibilityRow = (issueResolution, index) => {
    const statusMap = {
      exempt: {
        label: "Waived",
        badge: "badge-secondary",
        icon: <CircleOffIcon />,
        icon1: <InfoTooltipIcon stroke="#000000" />,
      },
      "not exempt": {
        label: "Applied",
        badge: "badge-danger",
        icon: <CircleArrowUpIcon />,
      },
      pending: {
        label: "Pending",
        badge: "badge-warning",
        icon: <ClockIcon />,
        icon1: <InfoTooltipIcon stroke="#000000" />,
      },
      approved: {
        label: "Approved",
        badge: "badge-success",
        icon: <CheckCircleIcon />,
      },
      rejected: {
        label: "Rejected",
        badge: "badge-danger",
        icon: <XCircleIcon />,
      },
      closed: {
        label: "Cancelled by client",
        badge: "badge-danger",
        icon: <XCircleIcon />,
        icon1: <InfoTooltipIcon stroke="#000000" />,
      },
    };

    const status = issueResolution?.status;
    const statusData = statusMap[status?.toLowerCase()];

    return (
      <tr key={index}>
        <td className="driver-avatar-cell">
          <div className="d-flex align-items-center g-12">
            <img
              src={getDriverAvatarSrc(issueResolution)}
              className="avatar-pic"
              alt="Driver avatar"
              onError={(e) => {
                e.target.src = AvatarImg;
              }}
            />
            <div className="text-capitalize">
              {issueResolution?.driverDetails?.firstname || "-"}
              <br />
              <span className="text-sm text-gray-600 ">
                {issueResolution?.driverDetails?.shortId}
              </span>
            </div>
          </div>
        </td>

        <td>
          <span
            className={` badge ${
              issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
              "approved"
                ? "badge-success"
                : issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
                  "rejected"
                ? "badge-gray"
                : issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
                  "pending"
                ? "badge-warning"
                : "badge-secondary"
            }`}
          >
            {issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
              "approved" && <ApprovedIcon />}

            {issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
              "rejected" && <RejectedIcon />}

            {issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
              "pending" && <PendingIcon />}

            {issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
            "approved"
              ? "Gets Paid"
              : issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
                "rejected"
              ? "Not eligible"
              : issueResolution?.jobDetails?.mavPaymentDetails?.verdict ===
                "pending"
              ? "Pending"
              : "-"}
          </span>
        </td>

        <td>
          <span>{getJobTypeDisplay(issueResolution?.jobDetails?.jobType)}</span>
          <br />
          <span className="text-xs text-gray-600">
            {issueResolution?.jobDetails?.shortId}
          </span>
        </td>
        <td>
          {statusData && (
            <span className={`badge ${statusData?.badge}`}>
              {statusData?.icon}
              <span className="ml-1">{statusData?.label}</span>
              <span
                className="tooltipStatus cursor-help"
                data-tooltip-id={`tooltip-${issueResolution?._id}`}
              >
                {statusData?.icon1}
              </span>

              <Tooltip
                id={`tooltip-${issueResolution?._id}`}
                className="custom-tooltip"
                place="top"
              >
                {issueResolution?.cancellationDetails?.reason}
              </Tooltip>
            </span>
          )}
        </td>
        <td className="driver-date-cell">
          {moment(issueResolution?.jobDetails?.bookedOn).format("DD-MM-YYYY")}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.jobDetails?.bookedOn).format("HH:mm")}
          </span>
        </td>

        <td>
          {moment(issueResolution?.jobDetails?.scheduledFor).format(
            "DD-MM-YYYY"
          ) || "-"}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.jobDetails?.scheduledFor).format(
              "HH:mm"
            ) || "-"}
          </span>
        </td>
        <td>
          {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
            "DD-MM-YYYY"
          ) || "-"}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
              "HH:mm"
            ) || "-"}
          </span>
        </td>

        <td>
          <Button
            className="btn btn-sm btn-secondary"
            onClick={toggleDrawer("right", true, "details", issueResolution)}
          >
            Review
          </Button>
        </td>
      </tr>
    );
  };

  const columns = useMemo(
    () => [
      { key: "driver", label: "Driver", sortable: true, sortKey: "firstnameSorter" },
      {
        key: "eligibilityStatus",
        label: "Eligibility Status",
        sortable: true,
        sortKey: "statusSorter",
      },
      { key: "job", label: "Job", sortable: true, sortKey: "eligibleOnSorter" },
      { key: "jobStatus", label: "Job status", sortable: true, sortKey: "statusqSorter" },
      { key: "postedOn", label: "Posted on", sortable: true, sortKey: "postedOnSorter" },
      {
        key: "scheduledFor",
        label: "Scheduled for",
        sortable: true,
        sortKey: "scheduledForSorter",
      },
      {
        key: "cancelledOn",
        label: "Cancelled on",
        sortable: true,
        sortKey: "cancelledOnSorter",
      },
      { key: "actions", label: "Actions", sortable: false },
    ],
    []
  );

  return (
    <>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        searchPlaceholder="Client, job ID, or reason..."
        onReset={onReset}
        filters={filters}
      />

      <DataTable
        data={issueResolutions}
        columns={columns}
        renderRow={renderEligibilityRow}
        onSort={onSort}
        getSortClass={getSortClass}
        emptyMessage="No Driver Pay Eligibility Records Found"
        colSpan={8}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={onPageChange}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={onPageSizeChange}
      />

      <Drawer
        anchor="right"
        open={state.right}
        onClose={toggleDrawer("right", false)}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          },
        }}
        className="issues-drawer"
      >
        {DriverPayEligibilityProps("right")}
      </Drawer>
    </>
  );
};

DriverPayEligibilityTab.propTypes = {
  issueResolutions: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  sort: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  inputValue: PropTypes.number.isRequired,
  setInputValue: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  DriverPayEligibilityProps: PropTypes.func.isRequired,
};

export default DriverPayEligibilityTab;
